/**
 * @file BillingClientView.js
 * @description This page displays billing details for a specific client with tabbed interface.
 */
import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './Billing.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import BillingDetailsTab from './tabs/BillingDetailsTab';
import BillingBillDetailTab from './tabs/BillingBillDetailTab';
import BillingServicesTab from './tabs/BillingServicesTab';
import BillingFeeBreakdownTab from './tabs/BillingFeeBreakdownTab';
import BillingSMSLogsTab from './tabs/BillingSMSLogsTab';
import BillingContractsTab from './tabs/BillingContractsTab';
import { getClient, getBillingSettings } from '../../services/clientService';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const TABS = {
    DETAILS: getText('msg_dc3decbb9384'),
    SERVICES: getText('msg_5cbd58404686'),
    FEE_BREAKDOWN: getText('msg_50f4cb804e74'),
    SMS_USAGE: getText('msg_e6bf96aa788c'),
    CONTRACTS: getText('msg_32767bc87ce3'),
    SMS_ALLOCATION: getText('msg_254f03dc3e4e'),
};

const BillingClientView = ({ isCreating = false }) => {
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();
    const { clientId } = useParams();
    const [activeTab, setActiveTab] = useState(TABS.DETAILS);
    const [clientData, setClientData] = useState({
        id: 0,
        name: '',
        code: '',
        status: '',
        agency: '',
        tier: '',
    });
    const [billingSettingsData, setBillingSettingsData] = useState(null);
    const [originalClientData, setOriginalClientData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [highlightedField, setHighlightedField] = useState(null);
    const [selectedServiceCount, setSelectedServiceCount] = useState(0);
    const servicesTabRef = useRef(null);
    const billDetailTabRef = useRef(null);
    const smsAllocationTabRef = useRef(null);

    const handleDeleteSelectedServices = () => {
        servicesTabRef.current?.deleteSelected();
    };

    useEffect(() => {
        if (clientId && !isCreating) {
            loadClientData(clientId);
        } else {
            setIsLoading(false);
        }
    }, [clientId, isCreating]);

    const loadClientData = async id => {
        try {
            setIsLoading(true);

            const fetchedClient = await getClient(id);
            setClientData(fetchedClient);
            setOriginalClientData(JSON.parse(JSON.stringify(fetchedClient)));

            await loadBillingData(id);
        } catch (error) {
            console.error('Failed to fetch client data', error);
            Toast.error(getText('msg_0883edac8103'));
        } finally {
            setIsLoading(false);
        }
    };

    const loadBillingData = async tenantId => {
        try {
            let billingSettings = null;

            try {
                billingSettings = await getBillingSettings(tenantId);
                setBillingSettingsData(billingSettings);
            } catch (error) {
                setBillingSettingsData(null);
            }

            const shouldBeCreateMode = !billingSettings;
            setIsCreateMode(shouldBeCreateMode);
        } catch (error) {
            console.error('Failed to load billing data', error);

            setIsCreateMode(true);
        }
    };

    const handleTabChange = tab => {
        setActiveTab(tab);
    };

    const handleClientDataChange = updatedData => {
        setClientData(prevData => ({
            ...prevData,
            ...updatedData,
        }));
    };

    const saveTab = async tabRef => {
        if (!tabRef.current) {
            return 'unchanged';
        }

        if (!tabRef.current.validate()) {
            return 'failed';
        }

        if (!tabRef.current.isDirty()) {
            return 'unchanged';
        }

        const didSave = await tabRef.current.save();
        return didSave ? 'saved' : 'failed';
    };

    const saveActiveTab = async () => {
        if (activeTab === TABS.DETAILS) {
            return saveTab(billDetailTabRef);
        }

        if (activeTab === TABS.SMS_ALLOCATION) {
            return saveTab(smsAllocationTabRef);
        }

        return 'unchanged';
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            const saveResult = await saveActiveTab();

            if (saveResult === 'saved') {
                Toast.success(getText('msg_39810ad2ba4b'));
            }

            if (saveResult === 'unchanged') {
                Toast.info(getText('msg_13c3a7ac4e47'));
            }
        } catch (error) {
            console.error('Failed to save billing data', error);
            const errorMessage =
                error.response?.data?.detail || getText('msg_f3a738211fb7');
            Toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleBillingDataRefresh = async () => {
        try {
            await loadBillingData(clientId);
        } catch (error) {
            console.error('Failed to refresh billing data', error);
        }
    };

    const handleClose = () => {
        if (checkUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => navigate(ROUTES.BILLING_CLIENTS),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTES.BILLING_CLIENTS);
        }
    };

    const checkUnsavedChanges = () => {
        if (!originalClientData) {
            return isCreating && (clientData.name || clientData.code);
        }

        const currentData = JSON.parse(JSON.stringify(clientData));
        const originalData = JSON.parse(JSON.stringify(originalClientData));

        for (const key in originalData) {
            if (
                typeof originalData[key] !== 'object' &&
                originalData[key] !== currentData[key]
            ) {
                return true;
            }
        }

        return false;
    };

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center position-relative pb-3 flex-column flex-xl-row gap-2">
                        <div>
                            <h2 className="page-title ms-4 m-0 col-auto col-lg-2 text-nowrap align-self-xl-baseline">
                                {isCreating || isCreateMode
                                    ? getText('msg_faa7b89b2b53')
                                    : `${clientData.name}`}
                            </h2>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_801458da18d6')}
                            </p>
                        </div>
                        <div
                            className="col-auto col-lg-2 d-flex justify-content-end justify-content-lg-evenly align-self-xl-baseline"
                            style={{ direction: 'rtl' }}
                        >
                            {isSaving ? (
                                <span style={{ direction: 'ltr' }}>
                                    {getText('msg_ae7e887517b0')}
                                </span>
                            ) : (
                                <>
                                    <span
                                        type="button"
                                        onClick={handleClose}
                                        className="ing-text-buttons me-3 d-flex"
                                        tabIndex={12}
                                    >
                                        <i className="bi bi-x me-2" />
                                        {getText('msg_bbfa773e5a63')}
                                    </span>
                                    <span
                                        type="button"
                                        onClick={handleSave}
                                        className="ing-text-buttons me-3 d-flex"
                                        disabled={isSaving}
                                        tabIndex={11}
                                    >
                                        <i className="bi bi-check me-2" />
                                        {getText('msg_efc007a393f6')}
                                    </span>
                                    {activeTab === TABS.SERVICES &&
                                        selectedServiceCount > 0 && (
                                            <span
                                                type="button"
                                                onClick={
                                                    handleDeleteSelectedServices
                                                }
                                                className="ing-text-buttons me-3 d-flex"
                                            >
                                                <i className="bi bi-trash me-2" />
                                                {getText('msg_f6fdbe48dc54')}
                                            </span>
                                        )}
                                </>
                            )}
                        </div>
                    </div>

                    {!isCreating && (
                        <div className="d-flex justify-content-between align-items-center position-relative mt-5">
                            <ul
                                className={`col-md-8 col-lg-8 ${styles.tabList}`}
                            >
                                {Object.values(TABS).map(tab => (
                                    <li
                                        key={tab}
                                        className={`${styles.tabItem} ${
                                            activeTab === tab
                                                ? styles.active
                                                : ''
                                        }`}
                                        onClick={() => handleTabChange(tab)}
                                    >
                                        {tab}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="w-100 mt-5 pb-5 text-center">
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    ) : (
                        <div
                            className={`w-100 mt-5 pb-5 ${styles.tabContainer}`}
                        >
                            {activeTab === TABS.DETAILS && (
                                <BillingBillDetailTab
                                    ref={billDetailTabRef}
                                    data={clientData}
                                    clientId={clientData.id}
                                    onRefresh={handleBillingDataRefresh}
                                />
                            )}
                            {activeTab === TABS.SERVICES && (
                                <BillingServicesTab
                                    ref={servicesTabRef}
                                    clientId={clientData.id}
                                    onSelectionChange={setSelectedServiceCount}
                                />
                            )}
                            {activeTab === TABS.FEE_BREAKDOWN && (
                                <BillingFeeBreakdownTab />
                            )}
                            {activeTab === TABS.SMS_USAGE && (
                                <BillingSMSLogsTab clientId={clientData.id} />
                            )}
                            {activeTab === TABS.CONTRACTS && (
                                <BillingContractsTab clientId={clientData.id} />
                            )}
                            {activeTab === TABS.SMS_ALLOCATION && (
                                <BillingDetailsTab
                                    ref={smsAllocationTabRef}
                                    data={clientData}
                                    clientId={clientData.id}
                                    billingSettings={billingSettingsData}
                                    isCreateMode={isCreateMode}
                                    onChange={handleClientDataChange}
                                    onRefresh={handleBillingDataRefresh}
                                    highlightedField={highlightedField}
                                    onFieldFocus={() =>
                                        setHighlightedField(null)
                                    }
                                    tenantTierId={clientData.tenantTierId}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BillingClientView;
